import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, LifeBuoy, Search, ShieldAlert, Wrench } from "lucide-react";
import { PaginationBar } from "@/components/PaginationBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  useResolveCallbacksData,
  useResolveDisputeEventsPage,
  useResolveFrictionData,
  useResolveHoldbacksData,
  useResolveIssuesData,
} from "@/hooks/useResolveServerPaging";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import { IssueService } from "@/services/issue.service";
import type { ResolveIssueQuickView, ResolveIssueRow, ResolveRange } from "@/services/resolve-page.service";
import type { IssueStatus } from "@/data/types";
import { toast } from "sonner";

const rangeOptions: ResolveRange[] = ["24h", "7d", "30d", "all"];
const statusOptions: IssueStatus[] = ["Open", "In Progress", "Resolved", "Closed"];

function fmt(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "MMM d, h:mm a");
}

function money(value: number) {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function toneClass(tone?: string) {
  if (tone === "destructive") return "border-destructive text-destructive";
  if (tone === "warning") return "border-warning text-warning";
  return "border-muted-foreground text-muted-foreground";
}

function EmptyTable({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-8 text-center text-xs text-muted-foreground">
        {label}
      </TableCell>
    </TableRow>
  );
}

function KpiCard({ label, value, tone = "text-foreground" }: { label: string; value: number; tone?: string }) {
  return (
    <Card className="shadow-none">
      <CardContent className="p-3 text-center">
        <p className={`text-xl font-bold ${tone}`}>{value}</p>
        <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

export function ResolveCallbacksServerPage() {
  const [range, setRange] = useState<ResolveRange>("7d");
  const [search, setSearch] = useState("");
  const pagination = useCountedServerPagination(25);
  const data = useResolveCallbacksData({ enabled: true, page: pagination.page, pageSize: pagination.pageSize, range, search });

  useEffect(() => pagination.reset(), [range, search, pagination.reset]);
  useEffect(() => pagination.setTotalItems(data.count), [data.count, pagination.setTotalItems]);

  const summary = data.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Callbacks</h1>
          <p className="text-xs text-muted-foreground">Server-paged QA callbacks grouped by age and attribution flags.</p>
        </div>
        <Select value={range} onValueChange={(value) => setRange(value as ResolveRange)}>
          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{rangeOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Last 24h" value={summary?.last24h ?? 0} tone="text-info" />
        <KpiCard label="Last 7d" value={summary?.last7d ?? 0} tone="text-warning" />
        <KpiCard label="Stale" value={summary?.stale ?? 0} tone="text-destructive" />
        <KpiCard label="High" value={summary?.high ?? 0} tone="text-destructive" />
        <KpiCard label="Unassigned" value={summary?.unassigned ?? 0} />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search job, installer, type..." className="h-8 pl-8 text-xs" />
      </div>

      <Card className="p-0 overflow-hidden shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Filed</TableHead>
              <TableHead>Job</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Installer</TableHead>
              <TableHead>Bucket</TableHead>
              <TableHead>Flag</TableHead>
              <TableHead>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.data.length === 0 ? <EmptyTable colSpan={8} label={data.loading ? "Loading callbacks..." : "No callbacks found."} /> : data.data.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(row.filedAt)}</TableCell>
                <TableCell className="font-mono text-[11px]">{row.jobLabel}</TableCell>
                <TableCell className="text-xs capitalize">{row.callbackType.replace(/_/g, " ")}</TableCell>
                <TableCell><Badge variant="outline" className={row.severity === "high" ? "border-destructive text-destructive" : ""}>{row.severity}</Badge></TableCell>
                <TableCell className="text-xs">{row.installerName}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{row.lifecycleBucket}</Badge></TableCell>
                <TableCell>{row.anomalyLabel ? <Badge variant="outline" className={toneClass(row.anomalyTone)}>{row.anomalyLabel}</Badge> : <span className="text-muted-foreground">-</span>}</TableCell>
                <TableCell className="max-w-[320px] truncate text-xs" title={row.description}>{row.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationBar {...pagination} />
      </Card>
    </div>
  );
}

export function ResolveHoldbacksServerPage() {
  const [bucket, setBucket] = useState<"all" | "active" | "paused" | "resolved">("all");
  const [search, setSearch] = useState("");
  const holdbackPagination = useCountedServerPagination(25);
  const disputePagination = useCountedServerPagination(15);
  const holdbacks = useResolveHoldbacksData({ enabled: true, page: holdbackPagination.page, pageSize: holdbackPagination.pageSize, bucket, search });
  const disputes = useResolveDisputeEventsPage({ enabled: true, page: disputePagination.page, pageSize: disputePagination.pageSize, search, refreshToken: holdbacks.refreshToken });

  useEffect(() => holdbackPagination.reset(), [bucket, search, holdbackPagination.reset]);
  useEffect(() => disputePagination.reset(), [search, disputePagination.reset]);
  useEffect(() => holdbackPagination.setTotalItems(holdbacks.count), [holdbacks.count, holdbackPagination.setTotalItems]);
  useEffect(() => disputePagination.setTotalItems(disputes.count), [disputes.count, disputePagination.setTotalItems]);

  const summary = holdbacks.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Holdbacks & Disputes</h1>
          <p className="text-xs text-muted-foreground">Derived holdback states plus append-only dispute events.</p>
        </div>
        <Select value={bucket} onValueChange={(value) => setBucket(value as typeof bucket)}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All states</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Active" value={summary?.activeHoldbacks ?? 0} tone="text-warning" />
        <KpiCard label="Paused" value={summary?.pausedHoldbacks ?? 0} tone="text-destructive" />
        <KpiCard label="Resolved" value={summary?.resolvedHoldbacks ?? 0} tone="text-success" />
        <KpiCard label="Reversals" value={summary?.reversals ?? 0} />
        <KpiCard label="Disputes" value={summary?.disputeOpenEvents ?? 0} />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search holdbacks or disputes..." className="h-8 pl-8 text-xs" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-0 overflow-hidden shadow-none">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">Holdbacks</div>
          <Table>
            <TableHeader><TableRow><TableHead>Last</TableHead><TableHead>Status</TableHead><TableHead>Job</TableHead><TableHead>Contractor</TableHead><TableHead className="text-right">Net</TableHead><TableHead>Flag</TableHead></TableRow></TableHeader>
            <TableBody>
              {holdbacks.data.length === 0 ? <EmptyTable colSpan={6} label={holdbacks.loading ? "Loading holdbacks..." : "No holdbacks found."} /> : holdbacks.data.map((row) => (
                <TableRow key={row.holdbackId}>
                  <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(row.lastEntryAt)}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{row.holdbackStatus}</Badge></TableCell>
                  <TableCell className="font-mono text-[11px]">{row.jobLabel}</TableCell>
                  <TableCell className="text-xs">{row.contractorName}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{money(row.netHeld)}</TableCell>
                  <TableCell>{row.anomalyLabel ? <Badge variant="outline" className={toneClass(row.anomalyTone)}>{row.anomalyLabel}</Badge> : <span className="text-muted-foreground">-</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationBar {...holdbackPagination} />
        </Card>

        <Card className="p-0 overflow-hidden shadow-none">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">Dispute Events</div>
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Type</TableHead><TableHead>Job</TableHead><TableHead>Contractor</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
            <TableBody>
              {disputes.data.length === 0 ? <EmptyTable colSpan={5} label={disputes.loading ? "Loading disputes..." : "No disputes found."} /> : disputes.data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(row.createdAt)}</TableCell>
                  <TableCell><Badge variant="outline">{row.eventType}</Badge></TableCell>
                  <TableCell className="font-mono text-[11px]">{row.jobLabel}</TableCell>
                  <TableCell className="text-xs">{row.contractorName}</TableCell>
                  <TableCell className="max-w-[240px] truncate text-xs" title={row.reason}>{row.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationBar {...disputePagination} />
        </Card>
      </div>
    </div>
  );
}

function issueBadgeClass(row: ResolveIssueRow) {
  if (row.isEscalated) return "border-destructive text-destructive";
  if (row.isUrgentActive) return "border-warning text-warning";
  return "";
}

export function ResolveIssuesServerPage() {
  const [quickView, setQuickView] = useState<ResolveIssueQuickView>("all");
  const [search, setSearch] = useState("");
  const [issueType, setIssueType] = useState("All");
  const [severity, setSeverity] = useState("All");
  const [branch, setBranch] = useState("all");
  const [region, setRegion] = useState("all");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const pagination = useCountedServerPagination(25);
  const data = useResolveIssuesData({ enabled: true, page: pagination.page, pageSize: pagination.pageSize, quickView, search, issueType, severity, branch, region });

  useEffect(() => pagination.reset(), [quickView, search, issueType, severity, branch, region, pagination.reset]);
  useEffect(() => pagination.setTotalItems(data.count), [data.count, pagination.setTotalItems]);

  const summary = data.summary;
  const quickViews = useMemo(() => [
    ["all", "All", summary?.total ?? 0],
    ["open", "Open", summary?.open ?? 0],
    ["urgent", "Urgent", summary?.urgent ?? 0],
    ["escalated", "Escalated", summary?.escalated ?? 0],
    ["crew", "Crew", summary?.crew ?? 0],
    ["sub", "Sub", summary?.subcontractor ?? 0],
    ["customer", "Customer", summary?.customer ?? 0],
    ["install", "Install", summary?.install ?? 0],
    ["material", "Material", summary?.material ?? 0],
    ["resolved", "Resolved", summary?.resolved ?? 0],
  ] as [ResolveIssueQuickView, string, number][], [summary]);

  const updateStatus = async (row: ResolveIssueRow, status: IssueStatus) => {
    setUpdatingId(row.issueId);
    const today = new Date().toISOString().split("T")[0];
    const patch: Partial<any> = { status };
    if (status === "In Progress") patch.dateInProgress = row.dateInProgress ?? today;
    if (status === "Resolved") patch.dateResolved = row.resolvedAt ?? today;
    if (status === "Closed") patch.dateClosed = row.dateClosed ?? today;
    const result = await IssueService.update(row.issueId, patch);
    setUpdatingId(null);
    if (result.error) {
      toast.error("Issue status update failed");
      return;
    }
    toast.success(`Issue ${row.issueId} updated`);
    data.refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Issues</h1>
          <p className="text-xs text-muted-foreground">Server-paged operational issues with denormalized job and customer context.</p>
        </div>
        <Button asChild className="gap-1.5"><Link to="/issue-form"><ShieldAlert className="h-3.5 w-3.5" /> Report Issue</Link></Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Open" value={summary?.open ?? 0} tone="text-warning" />
        <KpiCard label="Urgent" value={summary?.urgent ?? 0} tone="text-destructive" />
        <KpiCard label="Escalated" value={summary?.escalated ?? 0} tone="text-destructive" />
        <KpiCard label="In Progress" value={summary?.inProgress ?? 0} tone="text-info" />
        <KpiCard label="Resolved" value={summary?.resolved ?? 0} tone="text-success" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {quickViews.map(([key, label, count]) => (
          <Button key={key} variant={quickView === key ? "default" : "outline"} size="sm" className="h-7 text-[11px]" onClick={() => setQuickView(key)}>
            {label} {count ? `(${count})` : ""}
          </Button>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative max-w-xs flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search issues..." className="h-8 pl-8 text-xs" />
        </div>
        <Select value={issueType} onValueChange={setIssueType}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="All">All Types</SelectItem>{(summary?.issueTypes ?? []).map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="All">All Severity</SelectItem><SelectItem value="Urgent">Urgent</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
        </Select>
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Branches</SelectItem>{(summary?.branches ?? []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Regions</SelectItem>{(summary?.regions ?? []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card className="p-0 overflow-hidden shadow-none">
        <Table>
          <TableHeader><TableRow><TableHead>Issue</TableHead><TableHead>Job</TableHead><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Severity</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead><TableHead>Age</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.data.length === 0 ? (
              <EmptyTable
                colSpan={8}
                label={
                  data.loading
                    ? "Loading issues..."
                    : data.error
                      ? "Unable to load issues. Check the server-paged issues view."
                      : "No issues found."
                }
              />
            ) : data.data.map((row) => (
              <TableRow key={row.issueId}>
                <TableCell>
                  <div className="font-medium text-primary">{row.issueId}</div>
                  <div className="max-w-[260px] truncate text-[11px] text-muted-foreground" title={row.description}>{row.description}</div>
                </TableCell>
                <TableCell className="font-mono text-[11px]">{row.jobLabel}</TableCell>
                <TableCell className="text-xs">{row.customerName}</TableCell>
                <TableCell className="text-xs">{row.issueType}</TableCell>
                <TableCell><Badge variant="outline" className={issueBadgeClass(row)}>{row.severity}</Badge></TableCell>
                <TableCell>
                  <Select value={row.status} disabled={updatingId === row.issueId} onValueChange={(value) => void updateStatus(row, value as IssueStatus)}>
                    <SelectTrigger className="h-7 w-32 text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{statusOptions.map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs">{row.owner || "-"}</TableCell>
                <TableCell className="text-xs">{row.daysOpen}d</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationBar {...pagination} />
      </Card>
    </div>
  );
}

export function ResolveFrictionServerPage() {
  const [range, setRange] = useState<ResolveRange>("7d");
  const data = useResolveFrictionData({ enabled: true, range, pageSize: 50 });
  const summary = data.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Friction</h1>
          <p className="text-xs text-muted-foreground">Server-backed soft enforcement outcomes and repeat signal clusters.</p>
        </div>
        <Select value={range} onValueChange={(value) => setRange(value as ResolveRange)}>
          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{rangeOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KpiCard label="Dismissed" value={summary?.dismissed ?? 0} />
        <KpiCard label="Overridden" value={summary?.overridden ?? 0} tone="text-warning" />
        <KpiCard label="Expired" value={summary?.expired ?? 0} />
        <KpiCard label="Negative" value={summary?.negativeOverrides ?? 0} tone="text-destructive" />
        <KpiCard label="Repeat Disputes" value={summary?.repeatDisputeContractors ?? 0} />
        <KpiCard label="Repeat Reversals" value={summary?.repeatReversalContractors ?? 0} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-0 overflow-hidden shadow-none">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">Advisory outcomes</div>
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Outcome</TableHead><TableHead>Kind</TableHead><TableHead>Subject</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
            <TableBody>{data.advisoryPage.data.length === 0 ? <EmptyTable colSpan={5} label="No advisory outcomes." /> : data.advisoryPage.data.map((row) => (
              <TableRow key={row.id}><TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(row.eventAt)}</TableCell><TableCell><Badge variant="outline">{row.outcome}</Badge></TableCell><TableCell className="text-xs">{row.kind}</TableCell><TableCell className="font-mono text-[11px]">{row.subjectId}</TableCell><TableCell className="max-w-[220px] truncate text-xs" title={row.outcomeReason}>{row.outcomeReason || row.rationaleKey}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </Card>

        <Card className="p-0 overflow-hidden shadow-none">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">Negative overrides</div>
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Job</TableHead><TableHead>Reviewer</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
            <TableBody>{data.overridePage.data.length === 0 ? <EmptyTable colSpan={4} label="No negative overrides." /> : data.overridePage.data.map((row) => (
              <TableRow key={row.id}><TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{fmt(row.recordedAt)}</TableCell><TableCell className="font-mono text-[11px]">{row.jobLabel}</TableCell><TableCell className="text-xs">{row.reviewerRole || row.reviewerId}</TableCell><TableCell className="max-w-[260px] truncate text-xs" title={row.reason}>{row.reason}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </Card>

        <Card className="p-0 overflow-hidden shadow-none">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">Repeat dispute contractors</div>
          <Table>
            <TableHeader><TableRow><TableHead>Contractor</TableHead><TableHead className="text-right">Events</TableHead></TableRow></TableHeader>
            <TableBody>{data.repeatDisputes.data.length === 0 ? <EmptyTable colSpan={2} label="No repeat dispute clusters." /> : data.repeatDisputes.data.map((row) => (
              <TableRow key={row.contractorId}><TableCell className="text-xs">{row.contractorName}</TableCell><TableCell className="text-right font-mono text-xs">{row.eventCount}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </Card>

        <Card className="p-0 overflow-hidden shadow-none">
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">Repeat reversal contractors</div>
          <Table>
            <TableHeader><TableRow><TableHead>Contractor</TableHead><TableHead className="text-right">Reversals</TableHead></TableRow></TableHeader>
            <TableBody>{data.repeatReversals.data.length === 0 ? <EmptyTable colSpan={2} label="No repeat reversal clusters." /> : data.repeatReversals.data.map((row) => (
              <TableRow key={row.contractorId}><TableCell className="text-xs">{row.contractorName}</TableCell><TableCell className="text-right font-mono text-xs">{row.eventCount}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
